import { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import API from "../../services/api";
import { toast } from "react-toastify";
import { SlIconButton } from "@shoelace-style/shoelace/dist/react";
import Paginator from "../../shared/Paginator";
import debounce from "lodash.debounce";
import { Toast } from "../../shared/Toast";

const userRole_Options = [
  { value: 1, label: "Maker" },
  { value: 2, label: "Checker" },
  { value: 3, label: "Maker & Checker" },
];

const CST = () => {
  const [users, setUsers] = useState([]);
  const [editingRoles, setEditingRoles] = useState({});
  const [tempRoles, setTempRoles] = useState({});
  const [userRoles, setUserRoles] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");

  const loadData = async (page = 0, searchTerm = "") => {
    try {
      const result = await API({
        url: "/user/b2b_team/getCSTUsers",
        method: "POST",
        data: { page, search: searchTerm },
      });
      const { users = [], total = 0 } = result.data;
      setUsers(users);
      setTotalPages(Math.ceil(total / 10));
      const mapped = {};
      users.forEach((u) => {
        const role = userRole_Options.find((r) => r.value === u.mc_role);
        mapped[u.user_id] = role || null;
      });
      setUserRoles(mapped);
      setTempRoles(mapped);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadData(0,search);
  }, [search]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage]);

  const handlePageClick = (e) => setCurrentPage(e.selected);

  const startEditing = (id) => setEditingRoles((p) => ({ ...p, [id]: true }));
  const cancelEditing = (id) => setEditingRoles((p) => ({ ...p, [id]: false }));
  const handleChange = (id, opt) => setTempRoles((p) => ({ ...p, [id]: opt }));

  const saveRole = async (id) => {
    try {
      const role = tempRoles[id];
      const res = await API({
        url: `/user/b2b_team/user_role`,
        method: "POST",
        data: { cst_id: id, mc_role: role?.value },
      });
      if (res?.status === "SUCCESS") {
        Toast('success','Role Updated Successfully')
        setUserRoles((p) => ({ ...p, [id]: role }));
        setEditingRoles((p) => ({ ...p, [id]: false }));
        await loadData(0, search);
      } else Toast('error','Failed');
    } catch {
      Toast('error','Error updating');
    }
  };

  const debouncedSearch = useCallback(
    debounce((val) => setSearch(val), 500),
    []
  );

  return (
    <div className="bg-white shadow-md rounded-lg mx-2 mt-5">
      <div className="flex gap-4 justify-start items-center m-2">
        <span>Search : </span>
        <input type="text" placeholder="Search users..." className="border rounded px-3 py-1 w-full max-w-sm ring-2 hover:ring-blue-500" onChange={(e) => debouncedSearch(e.target.value)} />
      </div>
      <table className="min-w-full text-sm p-2">
        <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
          <tr>
            <th className="px-2 py-1 text-left">Name</th>
            <th className="px-2 py-1 text-left">Email</th>
            <th className="px-2 py-1 text-center">Role</th>
            <th className="px-2 py-1 text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-t hover:bg-gray-50">
              <td className="px-2 py-1">{u.name}</td>
              <td className="px-2 py-1 text-gray-600">{u.email || "-"}</td>
              <td className="px-2 py-1 text-center">
                {editingRoles[u.user_id] ? (
                  <Select options={userRole_Options} value={tempRoles[u.user_id] || null} onChange={(o) => handleChange(u.user_id, o)} styles={{ container: (b) => ({ ...b, minWidth: 150 }) }} menuPortalTarget={document.body} />
                ) : (
                  <span>{userRole_Options.find(role=>role.value===u.mc_role)?.label || <span className="text-gray-400">No Role</span>}</span>
                )}
              </td>
              <td className="px-2 py-1 text-center">
                {editingRoles[u.user_id] ? (
                  <div className="flex justify-center gap-2">
                    <button onClick={() => saveRole(u.user_id)} className="bg-green-500 hover:bg-green-600 text-white text-xs rounded px-2 py-1">
                      Save
                    </button>
                    <button onClick={() => cancelEditing(u.user_id)} className="bg-gray-400 hover:bg-gray-500 text-white text-xs rounded px-2 py-1">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <SlIconButton library="lucide" name="edit" onClick={() => startEditing(u.user_id)} className="text-blue-600 hover:text-blue-800" title="Edit" />
                )}
              </td>
            </tr>
          ))}
          {!users.length && (
            <tr>
              <td colSpan="4" className="text-center text-gray-500 py-6 italic">
                No users found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex justify-end mt-4">{totalPages > 1 && <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />}</div>
    </div>
  );
};

export default CST;
